import React, { useRef, useState } from "react";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { Document, Packer, Paragraph, HeadingLevel, Table, TableRow, TableCell, WidthType } from "docx";
import { downloadBlob } from "../nativeDownload";

export default function WhsOverview() {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const downloadAsWord = async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      const children = [];

      const addPara = (text, opts = {}) => {
        const clean = (text || "").replace(/\s+/g, " ").trim();
        if (!clean) return;
        children.push(new Paragraph({
          text: clean,
          heading: opts.heading,
          bullet: opts.bullet ? { level: 0 } : undefined,
          spacing: { after: 120 },
        }));
      };

      const buildTable = (tableEl) => {
        const rows = [];
        tableEl.querySelectorAll("tr").forEach((tr) => {
          const cells = [];
          const cellEls = tr.querySelectorAll("th,td");
          cellEls.forEach((cellEl) => {
            cells.push(new TableCell({
              children: [new Paragraph({ text: (cellEl.textContent || "").trim() })],
              width: { size: 100 / cellEls.length, type: WidthType.PERCENTAGE },
            }));
          });
          if (cells.length) rows.push(new TableRow({ children: cells }));
        });
        if (rows.length) children.push(new Table({ rows, width: { size: 100, type: WidthType.PERCENTAGE } }));
      };

      const walk = (node) => {
        Array.from(node.children).forEach((child) => {
          const tag = child.tagName.toLowerCase();
          if (tag === "h1") addPara(child.textContent, { heading: HeadingLevel.TITLE });
          else if (tag === "h2") addPara(child.textContent, { heading: HeadingLevel.HEADING_1 });
          else if (tag === "h3") addPara(child.textContent, { heading: HeadingLevel.HEADING_2 });
          else if (tag === "p") addPara(child.textContent);
          else if (tag === "table") buildTable(child);
          else if (tag === "ul" || tag === "ol") {
            Array.from(child.children).forEach((li) => addPara(li.textContent, { bullet: true }));
          } else if (child.children.length === 0 && (child.textContent || "").trim()) {
            addPara(child.textContent);
          } else {
            walk(child);
          }
        });
      };

      walk(contentRef.current);

      const doc = new Document({ sections: [{ children }] });
      const blob = await Packer.toBlob(doc);
      await downloadBlob(blob, "GSM-WHS-Technical-Overview.docx");
    } catch (e) {
      alert("Failed to generate Word document: " + e.message);
    } finally {
      setExporting(false);
    }
  };

  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h2 className="text-lg font-bold text-green-700 border-b border-green-200 pb-1 mb-3 mt-6">{title}</h2>
      {children}
    </div>
  );

  const Sub = ({ title, children }) => (
    <div className="mb-4">
      <h3 className="text-base font-bold text-green-800 mb-2">{title}</h3>
      {children}
    </div>
  );

  const Tip = ({ text }) => (
    <div className="bg-green-50 border-l-4 border-green-500 px-4 py-2 my-2 text-sm text-gray-700 italic">💡 {text}</div>
  );

  const Ul = ({ items }) => (
    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 mb-2">
      {items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{__html: item}} />)}
    </ul>
  );

  const Ol = ({ items }) => (
    <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 mb-2">
      {items.map((item, i) => <li key={i} dangerouslySetInnerHTML={{__html: item}} />)}
    </ol>
  );

  const P = ({ text }) => <p className="text-sm text-gray-700 mb-2" dangerouslySetInnerHTML={{__html: text}} />;

  const Tbl = ({ headers, rows }) => (
    <div className="overflow-x-auto my-3">
      <table className="w-full text-sm border-collapse">
        <thead className="bg-green-800 text-white">
          <tr>{headers.map((h,i) => <th key={i} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row,i) => (
            <tr key={i} className={i%2===0?"bg-white":"bg-green-50"}>
              {row.map((cell,j) => <td key={j} className="px-3 py-1.5 border-b border-gray-100 text-xs">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">GSM WHS Technical Overview</span>
          <button onClick={downloadAsWord} disabled={exporting} className="ml-auto bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-semibold px-4 py-1.5 rounded">
            {exporting ? "Generating..." : "⬇ Download as Word"}
          </button>
          <button onClick={() => navigate("/manual")} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-1.5 rounded">← Back to User Manual</button>
        </div>
        <div ref={contentRef} className="flex-1 overflow-auto px-8 py-6" style={{maxWidth:"900px"}}>

          <div className="text-center mb-8 pb-6 border-b border-gray-200">
            <div className="text-5xl mb-2">⛳</div>
            <h1 className="text-3xl font-bold text-green-800">GolfSocietyManager — WHS Technical Overview</h1>
            <div className="text-green-700 italic text-sm mt-1">How player handicaps and scores are calculated and verified</div>
          </div>

          <P text="This document describes, factually, how GolfSocietyManager (GSM) currently calculates player handicaps and handles score verification. It is provided for evaluation purposes ahead of a conversation about WHS integration — it is not a claim of official WHS accreditation, compliance, or endorsement. Any of the mechanisms described below can be reviewed, discussed or changed based on the relevant governing body's own requirements." />

          <Section title="1. Player Identity">
            <P text="Each player is a single record within their golf society's own GSM account. A player has a name, an email (optional, needed for the Player Portal), and a Handicap Index." />
          </Section>

          <Section title="2. Handicap Index (WHS HCP)">
            <P text="A player's Handicap Index is entered and maintained by the society organiser. It can be set from the player's official WHS Handicap Index, or — optionally, per society — adjusted automatically after each round using a configurable points-based system described in Section 8. This is a self-run alternative for societies without an official handicap authority behind every member, not a substitute for one where it exists." />
          </Section>

          <Section title="3. Tee Selection">
            <P text="Each player selects which tee they play from (e.g. White, Yellow, Red) for the linked course. The available tees are limited automatically to whatever that specific course's own recorded data supports, so a player cannot select a tee the course doesn't have." />
            <P text="Course data — including full tee-by-tee slope, rating and stroke index — is either imported from a real UK course database lookup, or read from a photographed scorecard and checked by the organiser." />
          </Section>

          <Section title="4. Course Handicap">
            <P text="Course Handicap is calculated automatically from the player's Handicap Index and their selected tee's slope and rating. If the society has set an HCP CAP for the event, Course Handicap is capped at that value before any further calculation." />
          </Section>

          <Section title="5. Format Allowance & Playing Handicap">
            <P text="Each competition format applies its own allowance to Course Handicap to produce the player's Playing Handicap for that event — the number actually used at the scorecard:" />
            <Tbl headers={["Format Family","Allowance"]} rows={[
              ["Singles Stableford / Medal / Stroke Play","95% of Course Handicap"],
              ["Singles Stableford (Full Handicap)","100% of Course Handicap"],
              ["Team Stableford / Medal","95% of Course Handicap"],
              ["Better Ball / Fourball","85% of Course Handicap"],
              ["Skins","100% of Course Handicap"],
              ["Foursomes","50% of the pair's combined Course Handicap"],
              ["Greensomes","60% of the lower player's Course Handicap + 40% of the higher"],
              ["Texas Scramble","25% / 20% / 15% / 10% per player, weighted from lowest to highest"],
            ]} />
            <P text="Match Play formats instead give the lowest-handicapped side or player in the match a scratch (zero) allowance, with every other side or player receiving the calculated difference in strokes — the standard match play convention." />
          </Section>

          <Section title="6. Score Entry">
            <P text="Gross scores are entered hole by hole through the app (online or via the offline desktop app, which syncs back once reconnected). Net scores and Stableford points are calculated automatically from each player's Playing Handicap and the course's stroke index for that hole." />
            <P text="An optional AI-assisted check can flag scores that look like likely data-entry mistakes (e.g. a score wildly inconsistent with the player's other holes) for the organiser to review before the round is finalised — it never changes a score itself." />
          </Section>

          <Section title="7. Marker Declaration & Verification">
            <P text="For eligible individual stroke-play formats, GSM includes a digital marker verification workflow: marker assignment is not set up by the organiser — the player, not the club or committee, chooses which playing partner verifies their score." />
            <Ol items={[
              "Before teeing off, players agree amongst themselves who is marking who, as normal",
              "Each player declares their chosen marker via the Player Portal",
              "After the round, the marker logs in and confirms the scorecard is correct for the player they declared themselves as marker for",
              "Once confirmed, that score is locked",
            ]} />
          </Section>

          <Section title="8. Score Locking & Audit Trail">
            <P text="Once a score is verified by its marker, it is locked and cannot be edited by the player or organiser through normal means. If a genuine error is found afterwards, an organiser can unlock it, but only by entering a mandatory reason — every unlock is permanently logged with that reason, a timestamp, and who performed it." />
          </Section>

          <Section title="9. Automatic Handicap Adjustment (Optional)">
            <P text="Separately from the WHS calculation chain above, a society can optionally turn on an automatic handicap adjustment system: a new member starts on their club handicap or a configured cap (whichever is lower), a maximum handicap is enforced, and after each archived round a player's Handicap Index adjusts automatically based on a configurable points-band table, with the competition winner receiving an additional reduction. This is intended for societies without an official handicap authority behind every member, and is off by default." />
          </Section>

          <Section title="10. Proposed Basis for WHS Submission">
            <P text="For societies where members already hold an official WHS Handicap Index, GSM's role in a submission workflow would be: hold that Handicap Index as entered, apply the tee-based Course Handicap and format-specific Playing Handicap calculations described above consistently for every round, capture the gross score hole by hole, and produce a marker-verified, timestamped, tamper-evident record of that score and its calculation — ready for GSM to export, or to submit via whatever mechanism the relevant WHS authority specifies (API, file export, or manual entry from the export). No specific submission mechanism is currently implemented; this section is a starting point for that conversation, not a description of an existing integration." />
            <Tip text="Everything in this document describes GSM's mechanism as it stands today. Any part of it — data format, verification requirements, submission timing — can be adapted to whatever the relevant WHS authority specifies." />
          </Section>

        </div>
      </div>
    </Layout>
  );
}
